/**
 * Theme Event System
 *
 * Central event bus and non-cart theme events.
 * Cart-specific events are in `_cart.actions.js`.
 *
 * @example Listening for an event
 *   import { ThemeEvents, eventBus } from '@theme/events';
 *   const unsubscribe = eventBus.on(ThemeEvents.variantSelected, (event) => {
 *     console.log(event.detail.resource.id);
 *   });
 */

/** Static string constants for non-cart theme event names */
export const ThemeEvents = Object.freeze({
	// ═══════════════════════════════════════════════════════════════════════════
	// VARIANT
	// ═══════════════════════════════════════════════════════════════════════════
	variantSelected: 'variant:selected',    // User selected a variant
	variantUpdated: 'variant:updated',      // Variant content re-rendered
	pdpSizeChosen: 'pdp:size-chosen',       // User picked a size in the PDP variant picker
	pdpSizeRequired: 'pdp:size-required',

	// ═══════════════════════════════════════════════════════════════════════════
	// COLLECTION
	// ═══════════════════════════════════════════════════════════════════════════
	collectionUpdated: 'collection:updated', // Products appended or filters applied

	// ═══════════════════════════════════════════════════════════════════════════
	// MEDIA
	// ═══════════════════════════════════════════════════════════════════════════
	mediaPlaying: 'media:playing',          // Media started playing

	// ═══════════════════════════════════════════════════════════════════════════
	// BACK IN STOCK
	// ═══════════════════════════════════════════════════════════════════════════
	backInStockRequested: 'back-in-stock:requested',   // User requested notification
	backInStockSubscribed: 'back-in-stock:subscribed', // Notification signup confirmed (no PII)

	// ═══════════════════════════════════════════════════════════════════════════
	// OVERLAY (mutual exclusion: drawer, mega menu, search)
	// ═══════════════════════════════════════════════════════════════════════════
	overlayOpened: 'overlay:opened',                 // A surface opened (close others)

	// ═══════════════════════════════════════════════════════════════════════════
	// SEARCH
	// ═══════════════════════════════════════════════════════════════════════════
	searchResultsUpdated: 'search:results-updated',  // Search results rendered for a query

	// ═══════════════════════════════════════════════════════════════════════════
	// NEWSLETTER
	// ═══════════════════════════════════════════════════════════════════════════
	newsletterSubscribed: 'newsletter:subscribed',   // Newsletter signup confirmed (no PII)
});

// ═══════════════════════════════════════════════════════════════════════════════
// VARIANT Event Classes
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Dispatched when a product variant is selected (e.g. swatch click, option change).
 *
 * @property {Object} detail
 * @property {Object} detail.resource
 * @property {string|number} detail.resource.id - Selected variant ID
 */
export class VariantSelectedEvent extends CustomEvent {
	/** @param {{ resource: { id: string|number } }} detail */
	constructor(detail) {
		super(ThemeEvents.variantSelected, { bubbles: true, detail });
	}
}

/**
 * Dispatched when variant-related content has been updated (e.g. section re-rendered).
 *
 * @property {Object} detail
 * @property {Object} detail.resource - Variant resource
 * @property {string} [detail.sourceId] - ID of the dispatching component
 * @property {Object} detail.data
 * @property {string} detail.data.html - Updated HTML content
 * @property {string|number} detail.data.productId - Product ID
 */
export class VariantUpdatedEvent extends CustomEvent {
	/** @param {{ resource?: Object, sourceId?: string, data: Object }} detail */
	constructor(detail) {
		super(ThemeEvents.variantUpdated, { bubbles: true, detail });
	}
}

/**
 * Dispatched when a user picks a size in the PDP variant picker dropdown.
 * Bubbles up to `action-product-context` which orchestrates the PDP UI state.
 *
 * @property {Object} detail
 * @property {Object} detail.data
 * @property {string|number} detail.data.variantId - Matched variant id (may belong to an unavailable variant)
 * @property {string} detail.data.value - The picked option value (e.g. "37.5")
 * @property {boolean} detail.data.available - Whether the variant is in stock
 * @property {string} [detail.data.stockLabel] - Stock label text ("Low Stock", "Notify Me", ...)
 */
export class PdpSizeChosenEvent extends CustomEvent {
	/** @param {{ data: { variantId: string|number, value: string, available: boolean, stockLabel?: string } }} detail */
	constructor(detail) {
		super(ThemeEvents.pdpSizeChosen, { bubbles: true, detail });
	}
}

export class PdpSizeRequiredEvent extends CustomEvent {
	constructor() {
		super(ThemeEvents.pdpSizeRequired, { bubbles: true });
	}
}

// ═══════════════════════════════════════════════════════════════════════════════
// COLLECTION Event Classes
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Dispatched when a collection page has been updated (e.g. products appended, filters applied).
 *
 * @property {Object} detail
 * @property {Object} detail.data
 * @property {string} detail.data.type - Update type ('products-appended', 'filters-applied')
 */
export class CollectionUpdatedEvent extends CustomEvent {
	/** @param {{ data: { type: string } }} detail */
	constructor(detail) {
		super(ThemeEvents.collectionUpdated, { bubbles: true, detail });
	}
}

// ═══════════════════════════════════════════════════════════════════════════════
// MEDIA Event Classes
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Dispatched when a media element begins playing.
 *
 * @property {Object} detail
 * @property {HTMLElement} detail.resource - The media element that started playing
 */
export class MediaPlayingEvent extends CustomEvent {
	/** @param {{ resource: HTMLElement }} detail */
	constructor(detail) {
		super(ThemeEvents.mediaPlaying, { bubbles: true, detail });
	}
}

// ═══════════════════════════════════════════════════════════════════════════════
// BACK IN STOCK Event Classes
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Dispatched when a user has requested back-in-stock notification (e.g. clicked unavailable variant).
 *
 * The event carries raw product/variant data. The modal component decides how to present it.
 *
 * @property {Object} detail
 * @property {Object} detail.data
 * @property {string} detail.data.productTitle - Product title
 * @property {string} detail.data.productUrl - Product URL
 * @property {string} detail.data.productImage - Product image URL
 * @property {string|number} detail.data.variantId - Variant ID
 * @property {string} detail.data.variantTitle - Variant title/label (e.g. size "38")
 * @property {HTMLElement} [detail.data.triggerElement] - The element that triggered the request (for focus return)
 */
export class BackInStockRequestedEvent extends CustomEvent {
	/** @param {{ data: Object }} detail */
	constructor(detail) {
		super(ThemeEvents.backInStockRequested, { bubbles: true, detail });
	}
}

/**
 * Dispatched after a back-in-stock notification signup succeeded.
 * Carries only ids — never the email address (no PII).
 *
 * @property {Object} detail
 * @property {Object} detail.data
 * @property {string|number} detail.data.variantId - Variant the visitor subscribed to
 * @property {string|number} [detail.data.productId] - Parent product id
 */
export class BackInStockSubscribedEvent extends CustomEvent {
	/** @param {{ data: { variantId: string|number, productId?: string|number } }} detail */
	constructor(detail) {
		super(ThemeEvents.backInStockSubscribed, { bubbles: true, detail });
	}
}

// ═══════════════════════════════════════════════════════════════════════════════
// OVERLAY Event Classes
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Dispatched when any UI surface opens (overlay, mega menu, search).
 * Listeners close themselves if the source is a different element (mutual exclusion).
 *
 * @property {Object} detail
 * @property {HTMLElement} detail.source - The element that opened
 */
export class OverlayOpenedEvent extends CustomEvent {
	/** @param {{ source: HTMLElement }} detail */
	constructor(detail) {
		super(ThemeEvents.overlayOpened, { bubbles: true, detail });
	}
}

// ═══════════════════════════════════════════════════════════════════════════════
// SEARCH Event Classes
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Dispatched after search results have been fetched and rendered for a query
 * (search overlay, predictive search).
 *
 * @property {Object} detail
 * @property {Object} detail.data
 * @property {string} detail.data.query - The search query
 * @property {number} detail.data.resultsCount - Number of rendered results
 */
export class SearchResultsUpdatedEvent extends CustomEvent {
	/** @param {{ data: { query: string, resultsCount: number } }} detail */
	constructor(detail) {
		super(ThemeEvents.searchResultsUpdated, { bubbles: true, detail });
	}
}

// ═══════════════════════════════════════════════════════════════════════════════
// NEWSLETTER Event Classes
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Dispatched after a successful newsletter subscription.
 * Carries only the signup surface — never the email address (no PII).
 *
 * @property {Object} detail
 * @property {Object} detail.data
 * @property {string} detail.data.source - Signup surface (e.g. 'newsletter-footer')
 */
export class NewsletterSubscribedEvent extends CustomEvent {
	/** @param {{ data: { source: string } }} detail */
	constructor(detail) {
		super(ThemeEvents.newsletterSubscribed, { bubbles: true, detail });
	}
}

// ═══════════════════════════════════════════════════════════════════════════════
// EVENT BUS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Centralized event bus for theme-wide communication.
 *
 * Wraps document event dispatching with:
 * - Debug logging (enabled via Theme Settings > Developer > Debug Events)
 * - Listener tracking for debugging
 * - Automatic unsubscribe via returned function
 *
 * @example
 *   import { eventBus } from '@theme/events';
 *   import { CartActions, CartAddEvent } from '@theme/cart--state--actions';
 *
 *   // Subscribe (returns unsubscribe function)
 *   const unsubscribe = eventBus.on(CartActions.add, (event) => {
 *     console.log('Add requested:', event.detail);
 *   });
 *
 *   // Emit (use typed event classes)
 *   eventBus.emit(new CartAddEvent({ data: { variantId: '123' } }));
 *
 *   // Cleanup
 *   unsubscribe();
 */
class EventBus {
	/** @type {boolean} */
	#debug;

	/** @type {Map<string, Set<Function>>} */
	#listeners = new Map();

	constructor() {
		// Debug mode enabled via Theme Settings > Developer > Debug Events
		this.#debug = window.__THEME_DEBUG_EVENTS__ ?? false;
	}

	/**
	 * Subscribe to an event.
	 * @param {string} eventType - Event type from CartActions or ThemeEvents
	 * @param {Function} handler - Event handler
	 * @returns {() => void} Unsubscribe function
	 */
	on(eventType, handler) {
		document.addEventListener(eventType, handler);

		if (!this.#listeners.has(eventType)) {
			this.#listeners.set(eventType, new Set());
		}
		this.#listeners.get(eventType).add(handler);

		return () => this.off(eventType, handler);
	}

	/**
	 * Unsubscribe from an event.
	 * @param {string} eventType
	 * @param {Function} handler
	 */
	off(eventType, handler) {
		document.removeEventListener(eventType, handler);
		this.#listeners.get(eventType)?.delete(handler);
	}

	/**
	 * Emit an event.
	 * @param {CustomEvent} event - Event instance (e.g. new CartAddEvent(...))
	 */
	emit(event) {
		if (this.#debug) {
			console.log(`[BUS] ${event.type}`, event.detail);
		}
		document.dispatchEvent(event);
	}

	/**
	 * Get listener counts by event type (for debugging).
	 * @returns {Object<string, number>}
	 */
	getListeners() {
		return Object.fromEntries(
			[...this.#listeners].map(([type, handlers]) => [type, handlers.size])
		);
	}
}

/** Singleton event bus for centralized event management */
export const eventBus = new EventBus();
